import buildingActions from 'actions/buildings/actions'
import placesActions from 'actions/places/actions'
import seoActions from 'actions/seo/actions'
import GridGallery from 'components/shared/gallery/venue-grid'
import ThumbnailLoader from 'components/shared/loaders/thumb'
import Inquire from 'components/shared/sticky/inquire'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import LazyLoad from 'react-lazyload'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

import BreadCrumbs from 'components/shared/bread-crumbs'
import InqueryRequest from 'components/shared/modals/handlers/inquery-request'
import LoadingOverlay from 'components/shared/loaders/loading-overlay'
import SuggestionBuildingCard from 'components/shared/card/vertical-suggestion'
import BuildingInformation from './information'
import BuildingMap from './map'
import HeadingInfo from './heading'
import LocationIcon from './heading/location-icon'
import OfficeTable from './offices-table'
import OfficeVenueCard from './offices-venue-cards'

const BuildingPage = ({
  match,
  details,
  suggestions,
  fetchBuildingDetailsRequest,
  fetchPlaceOptions,
  fetchSuggestionBuildingsRequest,
  highlights,
  title,
  public_transports = [],
  area_description = '',
}) => {
  const transports_data = public_transports.sort((a, b) => a?.distance - b?.distance)
  const { city, buildingId } = match.params
  const [isShowLoading, setShowLoading] = useState(true)
  const [shouldShowInquireModal, setShouldShowInquireModal] = useState(false)

  const data = useMemo(() => {
    if (!details) return undefined

    return details
  }, [details])

  if (!data) {
    return <LoadingOverlay loading />
  }

  useEffect(() => {
    const timer = setTimeout(() => setShowLoading(false), 300)

    return () => clearTimeout(timer)
  }, [])

  const { trails = [], name, address, images = [], flyspaces_expert } = data

  useEffect(() => {
    buildingId && fetchBuildingDetailsRequest(buildingId)
  }, [buildingId])

  useEffect(() => {
    details?.area_id &&
      details?.building_classification &&
      fetchSuggestionBuildingsRequest({
        id: details.id,
        area_id: details.area_id,
        building_classification: details.building_classification,
      })
    window.scrollTo({ behavior: 'smooth', top: 0 })
  }, [details])

  useEffect(() => {
    fetchPlaceOptions()
    // fetchSeoContent(location.pathname)
  }, [])

  return isShowLoading ? (
    <LoadingOverlay loading />
  ) : (
    <>
      <div className="building-page flex flex-col mb-20 lg:mb-0">
        <div className="w-full">
          {images.length > 0 && images[0] && (
            <div className="lg:hidden lg:w-0 block ">
              <GridGallery images={images} title={title} address={address} />
            </div>
          )}
          <div className="container mx-auto px-4">
            <div className="flex justify-between">
              <div className="lg:w-1/2 w-full lg:mt-0 mt-4">
                <BreadCrumbs trails={trails} />
                <HeadingInfo
                  highList={highlights}
                  buildingName={details?.name ?? ''}
                  className=""
                  address={address}
                  description={details.description}
                />
              </div>
              {!!images.length && images[0] && (
                <div className="lg:w-5/12 w-0 lg:block hidden">
                  <GridGallery
                    images={images}
                    title={title}
                    address={address}
                    heightFixed="750px"
                  />
                </div>
              )}
            </div>
            <OfficeVenueCard />
            <div id="building-inquire-form-id" className="flex flex-col lg:flex-row">
              <div className="w-full lg:w-8/12 my-5 lg:my-10 lg:pr-15px venue-left">
                <OfficeTable id={data.id} setShouldShowInquireModal={setShouldShowInquireModal} />
                <div className="building-separate md:mt-10 mt-8"></div>
                <BuildingInformation />
                <div className="building-separate mt-8 mb-6"></div>
                <div>
                  <h2 className="text-black font-bold text-2xl">Address</h2>
                  <div className="flex my-4 items-center">
                    <LocationIcon className="mr-2" />
                    <span className="block ">{address ?? ''}</span>
                  </div>
                  {transports_data.map(
                    (item, key) =>
                      key < 2 && (
                        <div key={key}>
                          <i
                            className=" fa fa-subway text-secondary-darkorange mr-2"
                            aria-hidden="true"
                          ></i>
                          {item?.distance && ` (${Math.floor(item?.distance)} m) `}to {item?.name}
                        </div>
                      )
                  )}
                </div>
                <div className={`w-full ${!suggestions.length ? 'h-full' : ''}} mt-6`}>
                  <LazyLoad
                    className="md:flex h-map relative items-stretch"
                    offset={100}
                    placeholder={<ThumbnailLoader />}
                  >
                    <div className="w-full">
                      <BuildingMap />
                    </div>
                  </LazyLoad>
                  <p
                    className="mt-3 location-description text-primary-black text-lg lg:text-base font-normal"
                    dangerouslySetInnerHTML={{ __html: area_description }}
                  ></p>
                </div>
                {!!suggestions.length && (
                  <div>
                    <div className="building-separate mt-8 mb-6"></div>
                    <div>
                      <h2 className="text-black font-bold text-xl">
                        You might be interested in these buildings with offices available for rent
                        in{' '}
                        {details?.trails?.[0]?.title ||
                          details?.building_information?.expert?.country}
                      </h2>
                    </div>
                    <div className="flex w-full flex-wrap">
                      {suggestions.map((building, key) => {
                        return (
                          <div
                            key={key}
                            className="xl:px-3 md:px-2 px-1 xl:pb-4 md:pb-3 pb-2 xl:w-3/12 lg:w-4/12 md:w-6/12 w-full"
                          >
                            <SuggestionBuildingCard
                              url={building.url}
                              name={building.name}
                              address={building.address}
                              averagePrice={building.averagePrice}
                              images={building.images}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="w-full lg:w-4/12 my-10 space-right hidden lg:block">
                <Inquire
                  showOption={false}
                  categorySlug={'building-page'}
                  title={name}
                  spaces={[]}
                  place={'place'}
                  flyspaces_expert={flyspaces_expert}
                  city={city}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <InqueryRequest
        isOpen={shouldShowInquireModal}
        toggle={() => {
          setShouldShowInquireModal(prev => !prev)
        }}
      >
        <div>
          <Inquire
            showOption={false}
            categorySlug={'building-page'}
            title={name}
            spaces={[]}
            place={'place'}
            flyspaces_expert={flyspaces_expert}
            city={city}
          />
        </div>
      </InqueryRequest>
    </>
  )
}

BuildingPage.propTypes = {
  match: PropTypes.object.isRequired,
  details: PropTypes.object,
  fetchBuildingDetailsRequest: PropTypes.func,
  fetchPlaceOptions: PropTypes.func,
  fetchSeoContent: PropTypes.func,
  fetchSuggestionBuildingsRequest: PropTypes.func,
  title: PropTypes.string,
  keywords: PropTypes.string,
  description: PropTypes.string,
  highlights: PropTypes.array,
  public_transports: PropTypes.array,
  listingVenues: PropTypes.object,
  area_description: PropTypes.string,
  suggestions: PropTypes.array,
}

const mapStateToProps = ({ building, seo }) => {
  return {
    details: building.public.details,
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    highlights: building.public.details.highlights,
    public_transports: building?.public?.details?.building_information?.public_transports,
    listingVenues: building.public.listingVenues,
    area_description: building.public.details.area_description,
    suggestions: building.public.suggestions,
  }
}

const mapDispatchToProps = {
  fetchBuildingDetailsRequest: buildingActions.fetchBuildingDetailsRequest,
  fetchSuggestionBuildingsRequest: buildingActions.fetchSuggestionBuildingsRequest,
  fetchPlaceOptions: placesActions.fetchPlaceOptions,
  fetchSeoContent: seoActions.fetchSeoContent,
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BuildingPage))
